// ABOUTME: Renders unified price/cost fields with pricelist/costlist override and selection dropdown for prototype options.
// ABOUTME: Manages display, dropdown, and custom input modes for price and cost editing in the prototype table.
import { Autocomplete, Box, Chip, RestoreIcon, Stack, Typography } from '@ds/coolshop';
import type { CustomCellRendererProps } from '@ds/data-table';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { NodeContentValueO } from '@src/__generated__/types';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import type { CellContext } from '@tanstack/react-table';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { LocaleNumberField } from '../../LocaleNumberField/LocaleNumberField';

const SELECTION_KEY_MAP = {
    '-1': 'excluded',
    '0': 'standard',
    '1': 'replaceable',
    '2': 'optional',
    '3': 'shipping option',
} as const;

const CUSTOM_KEY = '__custom__';

type IProtoValue = Record<string, string>;

export type ICellProtoOnChangeEventData = {
    nodeId: string;
    hierarchyId: string;
    initialValue: IProtoValue;
    oldValue: IProtoValue;
    newValue: IProtoValue;
};

export default function GridCellPrototypeRender(props: CustomCellRendererProps) {
    const { value: rawValue, row, params: rawParams } = props;

    const cellContext = rawParams as CellContext<any, any>;
    const events = cellContext.cell.column.columnDef.meta?.columEvents;
    const nodeId = row.id;
    const hierarchyId = row.hierarchy;
    const value = rawValue as NodeContentValueO[];

    const settings = useContext(SettingsContext);
    const pricingTargetName =
        settings.prototype?.contentTypes?.optionPrice?.contentType ?? 'optionPrice';
    const showPrice = !!settings.pimPricing?.enable;
    const pricelistOptions = cellContext.cell.column.columnDef.meta?.filter?.optionValues?.map(
        (ov: { label: string; value: string; currency?: string }) => ({
            id: ov.value,
            name: ov.label,
            code: ov.value,
            currency: ov.currency,
        }),
    );
    const costTargetName =
        settings.prototype?.contentTypes?.optionCost?.contentType ?? 'optionCost';
    const showCost =
        !!settings.pimPricing?.enable && !!settings.prototype?.contentTypes?.optionCost;
    const COST_TARGET = costTargetName;

    const intl = useIntl();
    const PRICE_TARGET = pricingTargetName;

    const [isSectionEdit, setIsSectionEdit] = useState(false);
    const [priceFieldMode, setPriceFieldMode] = useState<'display' | 'dropdown' | 'customInput'>(
        'display',
    );
    const [costFieldMode, setCostFieldMode] = useState<'display' | 'dropdown' | 'customInput'>(
        'display',
    );

    const initialValueMap = useMemo(
        () =>
            value.reduce((acc, v) => {
                if (v.code) {
                    acc[v.code as keyof IProtoValue] = v.value?.[0] || '';
                }
                return acc;
            }, {} as IProtoValue),
        [value],
    );

    const linkedPricelists: Array<{ id: string; price: number; lastPriceUpdate?: string }> =
        (() => {
            try {
                return JSON.parse(initialValueMap.linkedPricelists || '[]');
            } catch {
                return [];
            }
        })();

    const linkedCostlists: Array<{ id: string; cost: number }> = (() => {
        try {
            return JSON.parse(initialValueMap.linkedCostlists || '[]');
        } catch {
            return [];
        }
    })();
    const linkedCostMap = new Map(linkedCostlists.map(lc => [lc.id, lc.cost]));

    const defaultPriceListInfo: { id: string; name: string } | undefined = (() => {
        try {
            const raw = initialValueMap.defaultPriceList;
            return raw ? JSON.parse(raw) : undefined;
        } catch {
            return undefined;
        }
    })();

    const [valueMap, setValueMap] = useState<IProtoValue>(initialValueMap);

    // Re-sync local state whenever the underlying server value changes (autoselect
    // refetch, post-save refetch). Without this the display stays anchored to the
    // value that was present on first mount.
    useEffect(() => {
        setValueMap(initialValueMap);
    }, [initialValueMap]);

    const onOpenSelection = () => {
        setIsSectionEdit(true);
    };
    const onCloseSelection = () => {
        setIsSectionEdit(false);
    };
    const onChange = (data: Partial<IProtoValue>) => {
        setValueMap(p => {
            const newValue = {
                ...p,
                ...data,
            } as Record<string, string>;

            events?.onChange?.emit<ICellProtoOnChangeEventData>({
                hierarchyId,
                nodeId,
                initialValue: initialValueMap,
                oldValue: p,
                newValue,
            });
            return newValue;
        });
    };

    const defaultPriceListLabel = intl.formatMessage({
        id: 'prototype.pricelist.default',
        defaultMessage: 'Default',
    });

    const linkedPriceMap = new Map(linkedPricelists.map(lp => [lp.id, lp.price]));
    const linkedPriceLastUpdateMap = new Map(
        linkedPricelists
            .filter(lp => lp.lastPriceUpdate)
            .map(lp => [lp.id, lp.lastPriceUpdate as string]),
    );

    const pricelistCurrencyMap = new Map(
        (pricelistOptions ?? [])
            .filter((p): p is typeof p & { currency: string } => !!p.currency)
            .map(p => [p.id, p.currency]),
    );
    const defaultCurrency = defaultPriceListInfo?.id
        ? pricelistCurrencyMap.get(defaultPriceListInfo.id)
        : settings.prototype?.fallbackCurrency ?? undefined;

    type PriceSourceType =
        | 'custom'
        | 'pricelist'
        | 'prototype_override'
        | 'model_override'
        | 'base_option'
        | 'default';

    const priceSource: PriceSourceType = (() => {
        // User interactively set a custom price
        if (valueMap.priceListOverride === CUSTOM_KEY) return 'custom';
        // User interactively selected a pricelist override
        if (
            valueMap.priceListOverride &&
            valueMap.priceListOverride !== initialValueMap.priceListOverride
        ) {
            return 'pricelist';
        }
        // Use server-provided source when available
        const serverSource = initialValueMap.priceSource;
        if (
            serverSource === 'prototype_override' ||
            serverSource === 'model_override' ||
            serverSource === 'pricelist' ||
            serverSource === 'base_option'
        ) {
            return serverSource;
        }
        return 'default';
    })();

    const priceSourceLabel = (() => {
        if (priceSource === 'pricelist') {
            const selectedName = pricelistOptions?.find(
                p => p.id === valueMap.priceListOverride,
            )?.name;
            return selectedName ?? defaultPriceListInfo?.name ?? defaultPriceListLabel;
        }
        if (priceSource === 'custom') {
            return intl.formatMessage({
                id: 'prototype.price.source.custom',
                defaultMessage: 'Custom',
            });
        }
        if (priceSource === 'prototype_override') {
            return intl.formatMessage({
                id: 'prototype.price.source.prototypeOverride',
                defaultMessage: 'Prototype',
            });
        }
        if (priceSource === 'model_override') {
            return intl.formatMessage({
                id: 'prototype.price.source.modelOverride',
                defaultMessage: 'Model',
            });
        }
        if (priceSource === 'base_option') {
            return intl.formatMessage({
                id: 'prototype.price.source.baseOption',
                defaultMessage: 'Base',
            });
        }
        return intl.formatMessage({
            id: 'prototype.price.source.default',
            defaultMessage: 'Default',
        });
    })();

    const displayedPriceCurrency = (() => {
        if (priceSource === 'pricelist' && valueMap.priceListOverride) {
            return pricelistCurrencyMap.get(valueMap.priceListOverride) ?? defaultCurrency;
        }
        return defaultCurrency;
    })();

    const isPriceChanged =
        initialValueMap[PRICE_TARGET] !== valueMap[PRICE_TARGET] ||
        initialValueMap.priceListOverride !== valueMap.priceListOverride;

    const priceFieldOptions = pricelistOptions?.length
        ? [
              { key: '', label: defaultPriceListLabel },
              ...(linkedPricelists.length
                  ? pricelistOptions
                        .filter(pl => linkedPriceMap.has(pl.id))
                        .map(pl => {
                            const lastUpdate = linkedPriceLastUpdateMap.get(pl.id);
                            const formattedDate = lastUpdate ? intl.formatDate(lastUpdate) : '';
                            const suffix = formattedDate ? ` — ${formattedDate}` : '';
                            const price = linkedPriceMap.get(pl.id);
                            const formattedPrice =
                                price !== undefined ? intl.formatNumber(price) : '';
                            const currency = pricelistCurrencyMap.get(pl.id);
                            const priceWithCurrency = currency
                                ? `${formattedPrice} ${currency}`
                                : formattedPrice;
                            return {
                                key: pl.id,
                                label: `${pl.name} (${priceWithCurrency})${suffix}`,
                            };
                        })
                  : pricelistOptions.map(pl => ({
                        key: pl.id,
                        label: pl.currency ? `${pl.name} — ${pl.currency}` : pl.name,
                    }))),
              {
                  key: CUSTOM_KEY,
                  label: intl.formatMessage({
                      id: 'prototype.price.custom',
                      defaultMessage: 'Custom price',
                  }),
              },
          ]
        : [];

    const handlePriceFieldClick = () => {
        if (pricelistOptions?.length) {
            setPriceFieldMode('dropdown');
        } else {
            setPriceFieldMode('customInput');
        }
    };

    const handleDropdownChange = (_: unknown, newValue: { key: string; label: string } | null) => {
        if (!newValue) return;

        if (newValue.key === CUSTOM_KEY) {
            setPriceFieldMode('customInput');
            onChange({ priceListOverride: CUSTOM_KEY });
            return;
        }

        if (newValue.key === '') {
            const defaultPriceFromList = defaultPriceListInfo?.id
                ? linkedPriceMap.get(defaultPriceListInfo.id)
                : undefined;
            onChange({
                [PRICE_TARGET]:
                    defaultPriceFromList !== undefined ? String(defaultPriceFromList) : '',
                priceListOverride: '',
            });
        } else {
            const linkedPrice = linkedPriceMap.get(newValue.key);
            onChange({
                priceListOverride: newValue.key,
                ...(linkedPrice !== undefined ? { [PRICE_TARGET]: String(linkedPrice) } : {}),
            });
        }
        setPriceFieldMode('display');
    };

    const handleRestore = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange({
            [PRICE_TARGET]: initialValueMap[PRICE_TARGET],
            priceListOverride: initialValueMap.priceListOverride,
        });
    };

    // Cost source logic (mirrors price source)
    const costSource: PriceSourceType = (() => {
        if (valueMap.costListOverride === CUSTOM_KEY) return 'custom';
        if (
            valueMap.costListOverride &&
            valueMap.costListOverride !== initialValueMap.costListOverride
        ) {
            return 'pricelist';
        }
        const serverSource = initialValueMap.costSource;
        if (
            serverSource === 'prototype_override' ||
            serverSource === 'model_override' ||
            serverSource === 'pricelist' ||
            serverSource === 'base_option'
        ) {
            return serverSource;
        }
        return 'default';
    })();

    const costSourceLabel = (() => {
        if (costSource === 'pricelist') {
            const selectedName = pricelistOptions?.find(
                p => p.id === valueMap.costListOverride,
            )?.name;
            return selectedName ?? defaultPriceListInfo?.name ?? defaultPriceListLabel;
        }
        if (costSource === 'custom') {
            return intl.formatMessage({
                id: 'prototype.cost.source.custom',
                defaultMessage: 'Custom',
            });
        }
        if (costSource === 'prototype_override') {
            return intl.formatMessage({
                id: 'prototype.cost.source.prototypeOverride',
                defaultMessage: 'Prototype',
            });
        }
        if (costSource === 'model_override') {
            return intl.formatMessage({
                id: 'prototype.cost.source.modelOverride',
                defaultMessage: 'Model',
            });
        }
        if (costSource === 'base_option') {
            return intl.formatMessage({
                id: 'prototype.cost.source.baseOption',
                defaultMessage: 'Base',
            });
        }
        return intl.formatMessage({
            id: 'prototype.cost.source.default',
            defaultMessage: 'Default',
        });
    })();

    const displayedCostCurrency = (() => {
        if (costSource === 'pricelist' && valueMap.costListOverride) {
            return pricelistCurrencyMap.get(valueMap.costListOverride) ?? defaultCurrency;
        }
        return defaultCurrency;
    })();

    const isCostChanged =
        initialValueMap[COST_TARGET] !== valueMap[COST_TARGET] ||
        initialValueMap.costListOverride !== valueMap.costListOverride;

    const costFieldOptions = pricelistOptions?.length
        ? [
              { key: '', label: defaultPriceListLabel },
              ...(linkedCostlists.length
                  ? pricelistOptions
                        .filter(pl => linkedCostMap.has(pl.id))
                        .map(pl => {
                            const cost = linkedCostMap.get(pl.id);
                            const currency = pricelistCurrencyMap.get(pl.id);
                            const costWithCurrency = currency ? `${cost} ${currency}` : `${cost}`;
                            return {
                                key: pl.id,
                                label: `${pl.name} (${costWithCurrency})`,
                            };
                        })
                  : pricelistOptions.map(pl => ({
                        key: pl.id,
                        label: pl.currency ? `${pl.name} — ${pl.currency}` : pl.name,
                    }))),
              {
                  key: CUSTOM_KEY,
                  label: intl.formatMessage({
                      id: 'prototype.cost.custom',
                      defaultMessage: 'Custom cost',
                  }),
              },
          ]
        : [];

    const handleCostFieldClick = () => {
        if (pricelistOptions?.length) {
            setCostFieldMode('dropdown');
        } else {
            setCostFieldMode('customInput');
        }
    };

    const handleCostDropdownChange = (
        _: unknown,
        newValue: { key: string; label: string } | null,
    ) => {
        if (!newValue) return;

        if (newValue.key === CUSTOM_KEY) {
            setCostFieldMode('customInput');
            onChange({ costListOverride: CUSTOM_KEY });
            return;
        }

        if (newValue.key === '') {
            const defaultCostFromList = defaultPriceListInfo?.id
                ? linkedCostMap.get(defaultPriceListInfo.id)
                : undefined;
            onChange({
                [COST_TARGET]: defaultCostFromList !== undefined ? String(defaultCostFromList) : '',
                costListOverride: '',
            });
        } else {
            const linkedCost = linkedCostMap.get(newValue.key);
            onChange({
                costListOverride: newValue.key,
                ...(linkedCost !== undefined ? { [COST_TARGET]: String(linkedCost) } : {}),
            });
        }
        setCostFieldMode('display');
    };

    const handleCostRestore = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange({
            [COST_TARGET]: initialValueMap[COST_TARGET],
            costListOverride: initialValueMap.costListOverride,
        });
    };

    return (
        <Stack direction="row" gap={3}>
            {showPrice && (
                <Stack justifyContent="center" minWidth={120}>
                    {priceFieldMode === 'display' && (
                        <Stack
                            direction="row"
                            gap={1}
                            onClick={handlePriceFieldClick}
                            alignItems="center"
                            cursor="pointer"
                        >
                            <Stack>
                                <Typography variant="body2">
                                    {valueMap[PRICE_TARGET]}
                                    {displayedPriceCurrency ? ` ${displayedPriceCurrency}` : ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {priceSourceLabel}
                                </Typography>
                            </Stack>
                            {isPriceChanged && (
                                <Box display="flex" onClick={handleRestore} cursor="pointer">
                                    <RestoreIcon fontSize="small" color="action" />
                                </Box>
                            )}
                        </Stack>
                    )}
                    {priceFieldMode === 'dropdown' && (
                        <ClickAwayListener onClickAway={() => setPriceFieldMode('display')}>
                            <Autocomplete
                                minWidth={180}
                                size="small"
                                value={(() => {
                                    if (valueMap.priceListOverride === CUSTOM_KEY) {
                                        const customOpt = priceFieldOptions.find(
                                            o => o.key === CUSTOM_KEY,
                                        );
                                        if (customOpt) return customOpt;
                                    }
                                    if (valueMap.priceListOverride) {
                                        const match = priceFieldOptions.find(
                                            o => o.key === valueMap.priceListOverride,
                                        );
                                        if (match) return match;
                                    }
                                    return (
                                        priceFieldOptions[0] ?? {
                                            key: '',
                                            label: defaultPriceListLabel,
                                        }
                                    );
                                })()}
                                options={priceFieldOptions}
                                getOptionLabel={option => option.label}
                                isOptionEqualToValue={(opt, v) => opt.key === v.key}
                                blurOnSelect
                                open
                                disableClearable
                                onClose={() => {
                                    setPriceFieldMode(prev =>
                                        prev === 'dropdown' ? 'display' : prev,
                                    );
                                }}
                                onChange={handleDropdownChange}
                            />
                        </ClickAwayListener>
                    )}
                    {priceFieldMode === 'customInput' && (
                        <LocaleNumberField
                            size="small"
                            autoFocus
                            value={valueMap[PRICE_TARGET]}
                            endAdornment={displayedPriceCurrency || undefined}
                            onChange={newValue => {
                                onChange({
                                    [PRICE_TARGET]: newValue,
                                    priceListOverride: CUSTOM_KEY,
                                });
                            }}
                            onBlur={() => {
                                setPriceFieldMode('display');
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === 'Escape') {
                                    setPriceFieldMode('display');
                                }
                            }}
                            onFocus={e => {
                                // the field swaps the grouped display for the plain value on
                                // focus: select once that has been applied
                                const input = e.target as HTMLInputElement;
                                requestAnimationFrame(() => input.select());
                            }}
                        />
                    )}
                </Stack>
            )}
            {showCost && (
                <Stack justifyContent="center" minWidth={120}>
                    {costFieldMode === 'display' && (
                        <Stack
                            direction="row"
                            gap={1}
                            onClick={handleCostFieldClick}
                            alignItems="center"
                            cursor="pointer"
                        >
                            <Stack>
                                <Typography variant="body2">
                                    {valueMap[COST_TARGET]}
                                    {displayedCostCurrency ? ` ${displayedCostCurrency}` : ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {costSourceLabel}
                                </Typography>
                            </Stack>
                            {isCostChanged && (
                                <Box display="flex" onClick={handleCostRestore} cursor="pointer">
                                    <RestoreIcon fontSize="small" color="action" />
                                </Box>
                            )}
                        </Stack>
                    )}
                    {costFieldMode === 'dropdown' && (
                        <ClickAwayListener onClickAway={() => setCostFieldMode('display')}>
                            <Autocomplete
                                minWidth={180}
                                size="small"
                                value={(() => {
                                    if (valueMap.costListOverride === CUSTOM_KEY) {
                                        const customOpt = costFieldOptions.find(
                                            o => o.key === CUSTOM_KEY,
                                        );
                                        if (customOpt) return customOpt;
                                    }
                                    if (valueMap.costListOverride) {
                                        const match = costFieldOptions.find(
                                            o => o.key === valueMap.costListOverride,
                                        );
                                        if (match) return match;
                                    }
                                    return (
                                        costFieldOptions[0] ?? {
                                            key: '',
                                            label: defaultPriceListLabel,
                                        }
                                    );
                                })()}
                                options={costFieldOptions}
                                getOptionLabel={option => option.label}
                                isOptionEqualToValue={(opt, v) => opt.key === v.key}
                                blurOnSelect
                                open
                                disableClearable
                                onClose={() => {
                                    setCostFieldMode(prev =>
                                        prev === 'dropdown' ? 'display' : prev,
                                    );
                                }}
                                onChange={handleCostDropdownChange}
                            />
                        </ClickAwayListener>
                    )}
                    {costFieldMode === 'customInput' && (
                        <LocaleNumberField
                            size="small"
                            autoFocus
                            value={valueMap[COST_TARGET]}
                            endAdornment={displayedCostCurrency || undefined}
                            onChange={newValue => {
                                onChange({
                                    [COST_TARGET]: newValue,
                                    costListOverride: CUSTOM_KEY,
                                });
                            }}
                            onBlur={() => {
                                setCostFieldMode('display');
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === 'Escape') {
                                    setCostFieldMode('display');
                                }
                            }}
                            onFocus={e => {
                                // the field swaps the grouped display for the plain value on
                                // focus: select once that has been applied
                                const input = e.target as HTMLInputElement;
                                requestAnimationFrame(() => input.select());
                            }}
                        />
                    )}
                </Stack>
            )}
            <Stack justifyContent="center">
                {!isSectionEdit ? (
                    <Chip
                        label={
                            SELECTION_KEY_MAP[valueMap.selection as keyof typeof SELECTION_KEY_MAP]
                        }
                        size="small"
                        onClick={onOpenSelection}
                        deleteIcon={<RestoreIcon />}
                        onDelete={
                            initialValueMap.selection !== valueMap.selection
                                ? () => {
                                      onChange({
                                          selection: initialValueMap.selection,
                                      });
                                  }
                                : undefined
                        }
                    />
                ) : (
                    <ClickAwayListener onClickAway={onCloseSelection}>
                        <Autocomplete
                            minWidth={130}
                            size="small"
                            value={{
                                key: valueMap.selection,
                                label: SELECTION_KEY_MAP[
                                    valueMap.selection as keyof typeof SELECTION_KEY_MAP
                                ],
                            }}
                            options={Object.entries(SELECTION_KEY_MAP).map(([key, label]) => ({
                                key,
                                label,
                            }))}
                            getOptionLabel={option => option.label}
                            isOptionEqualToValue={(opt, v) => opt.key === v.key}
                            blurOnSelect
                            open
                            disableClearable
                            onClose={onCloseSelection}
                            onChange={(_, newValue) => {
                                if (!newValue) return;
                                onChange({
                                    selection: newValue.key,
                                });
                            }}
                        />
                    </ClickAwayListener>
                )}
            </Stack>
        </Stack>
    );
}
